var _excluded = ["className", "children", "onStepChanged", "currentStep", "isNumbered", "showCompleteStepIcon", "showMobileStepCount", "showNumberCircles", "completedStepIcon"],
  _excluded2 = ["_stepNumber", "disabled"],
  _excluded3 = ["className", "children", "onClick"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import { forwardRef, omitThemingProps, tqc, useMultiStyleConfig } from '../../system';
import { Text } from '../layout';
import { createContext, cx, isBoolean } from '../../utils';
import { FaCheckCircle } from 'react-icons/fa';
var getStepVariant = (step, activeStep) => {
  return step > activeStep ? 'default' : step === activeStep ? 'active' : 'completed';
};
export var getStepCountMessage = (stepIndex, maxStep) => {
  if (stepIndex === undefined || maxStep === undefined) {
    return '';
  }
  return "Step ".concat(Math.min(stepIndex + 1, maxStep), " of ").concat(maxStep);
};
var [StepContextProvider, useStepContext] = createContext({
  name: 'StepsContext',
  errorMessage: 'useStepContext: `context` is undefined. Seems you forgot to wrap all step components within <Steps />'
});
export var Steps = forwardRef((props, ref) => {
  var _props$size;
  var _omitThemingProps = omitThemingProps(props),
    {
      className,
      children,
      onStepChanged,
      currentStep,
      isNumbered = true,
      showCompleteStepIcon = true,
      showMobileStepCount = true,
      showNumberCircles = true,
      completedStepIcon
    } = _omitThemingProps,
    rest = _objectWithoutProperties(_omitThemingProps, _excluded);
  var stepIcon = completedStepIcon !== undefined ? completedStepIcon : /*#__PURE__*/React.createElement(FaCheckCircle, null);
  var _className = cx(className, 'tqc-steps');
  var styles = useMultiStyleConfig('Steps', rest);
  var containerStyles = _objectSpread(_objectSpread({}, styles.container), {}, {
    width: 'auto',
    display: 'flex',
    flexWrap: 'wrap'
  });
  var stepCountStyles = _objectSpread(_objectSpread({}, styles.stepCount), {}, {
    display: ['block', 'none'],
    fontWeight: 'bold',
    mb: '1.25rem',
    fontSize: '1rem'
  });
  return /*#__PURE__*/React.createElement(StepContextProvider, {
    value: {
      onStepChanged,
      currentStep: currentStep !== null && currentStep !== void 0 ? currentStep : Number.MAX_VALUE,
      size: (_props$size = props.size) !== null && _props$size !== void 0 ? _props$size : 'md',
      isNumbered,
      showCompleteStepIcon,
      completedStepIcon: stepIcon,
      showNumberCircles
    }
  }, showMobileStepCount && /*#__PURE__*/React.createElement(Text, {
    sx: stepCountStyles
  }, getStepCountMessage(currentStep, children === null || children === void 0 ? void 0 : children.length)), /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref,
    __css: containerStyles,
    className: _className
  }, rest), React.Children.map(children, (child, index) => /*#__PURE__*/React.isValidElement(child) && /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
    _stepNumber: index
  })))));
});
export var Step = forwardRef((_ref, ref) => {
  var {
      _stepNumber = 0,
      disabled: disabledProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded2);
  var {
    currentStep,
    size,
    completedStepIcon,
    showCompleteStepIcon,
    showNumberCircles,
    isNumbered,
    onStepChanged
  } = useStepContext();
  var isComplete = _stepNumber < currentStep;
  var isCurrent = _stepNumber + 1 === currentStep;
  var disabled = isBoolean(disabledProp) ? disabledProp : _stepNumber >= currentStep;
  var styles = useMultiStyleConfig('Steps', _objectSpread(_objectSpread({}, props), {}, {
    variant: getStepVariant(_stepNumber, currentStep),
    size
  }));
  var _omitThemingProps2 = omitThemingProps(props),
    {
      className,
      children,
      onClick
    } = _omitThemingProps2,
    rest = _objectWithoutProperties(_omitThemingProps2, _excluded3);
  var _className = cx(className, "tqc-steps__step ".concat(isCurrent ? 'tqc-steps__current' : ''));
  var handleClick = event => {
    onClick && onClick(event);
    onStepChanged && onStepChanged(_stepNumber);
  };
  return /*#__PURE__*/React.createElement(tqc.button, _extends({
    onClick: handleClick,
    __css: styles.step,
    className: _className,
    ref: ref,
    disabled: disabled,
    tabIndex: disabled ? -1 : 0
  }, rest), /*#__PURE__*/React.createElement(tqc.span, {
    className: "tqc-steps__step-status"
  }, isComplete && showCompleteStepIcon ? /*#__PURE__*/React.createElement(tqc.span, {
    className: "tqc-steps__step-icon",
    __css: styles.icon
  }, completedStepIcon) : isNumbered && showNumberCircles ? /*#__PURE__*/React.createElement(tqc.span, {
    className: "tqc-steps__step-count",
    sx: styles.count
  }, _stepNumber + 1) : isNumbered && !showNumberCircles ? /*#__PURE__*/React.createElement(tqc.span, {
    className: "tqc-steps__step-count",
    __css: styles.noCircle
  }, _stepNumber + 1 + '.') : null), /*#__PURE__*/React.createElement(tqc.span, {
    className: "tqc-steps__step-text"
  }, children));
});